import { AddPhotoAlternateOutlined, DeleteOutline } from '@mui/icons-material'
import { Box, Grid, Typography } from '@mui/material'
import React, {
  ChangeEvent,
  DragEvent,
  Fragment,
  MutableRefObject,
  useCallback,
  useRef,
  useState,
} from 'react'
import { UseFormReturn } from 'react-hook-form'
import ModalCropTheme from '../modal-crop'
import { useStyles } from './styles'

type FileType = 'principal' | 'branco' | 'preto'

interface ILogoUploadProps {
  formData: UseFormReturn<any>
}

const LogoUpload: React.FC<ILogoUploadProps> = ({ formData: { setValue, watch } }) => {
  const [modalOpen, setModalOpen] = useState<{
    baseLogoOpen: boolean
    whiteLogoOpen: boolean
    blackLogoOpen: boolean
  }>({
    baseLogoOpen: false,
    whiteLogoOpen: false,
    blackLogoOpen: false,
  })

  const principalLogoRef = useRef<HTMLInputElement>(null)
  const whiteLogoRef = useRef<HTMLInputElement>(null)
  const blackLogoRef = useRef<HTMLInputElement>(null)

  const createUrlPreview = (file: File, name: string) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      setValue(name, e.target!.result)
    }

    reader.readAsDataURL(file)
  }

  const handleAddFile = (e: ChangeEvent<HTMLInputElement>, type: FileType) => {
    const filesFromForm = e.target.files

    if (filesFromForm) {
      if (type === 'principal') {
        createUrlPreview(filesFromForm[0], 'baseLogo')
        setValue('baseLogoFile', filesFromForm[0])
        setModalOpen((prev) => ({ ...prev, baseLogoOpen: true }))
      } else if (type === 'branco') {
        createUrlPreview(filesFromForm[0], 'whiteLogo')
        setValue('whiteLogoFile', filesFromForm[0])
        setModalOpen((prev) => ({ ...prev, whiteLogoOpen: true }))
      } else if (type === 'preto') {
        createUrlPreview(filesFromForm[0], 'blackLogo')
        setValue('blackLogoFile', filesFromForm[0])
        setModalOpen((prev) => ({ ...prev, blackLogoOpen: true }))
      }
    }
  }

  const handleRemoveFile = (type: FileType) => {
    if (type === 'principal') {
      setValue('baseLogoFile', '')
      setValue('baseLogo', null)
      principalLogoRef.current!.value = ''
    } else if (type === 'branco') {
      setValue('whiteLogoFile', '')
      setValue('whiteLogo', null)
      whiteLogoRef.current!.value = ''
    } else if (type === 'preto') {
      setValue('blackLogoFile', '')
      setValue('blackLogo', null)
      blackLogoRef.current!.value = ''
    }
  }

  const handleFileSelect = (e: DragEvent<HTMLDivElement>, type: FileType) => {
    e.preventDefault()
    const files: FileList = e.dataTransfer.files

    if (type === 'principal') {
      createUrlPreview(files[0], 'baseLogo')
      setValue('baseLogoFile', files[0])
      setModalOpen((prev) => ({ ...prev, baseLogoOpen: true }))
    } else if (type === 'branco') {
      createUrlPreview(files[0], 'whiteLogo')
      setValue('whiteLogoFile', files[0])
      setModalOpen((prev) => ({ ...prev, whiteLogoOpen: true }))
    } else if (type === 'preto') {
      createUrlPreview(files[0], 'blackLogo')
      setValue('blackLogoFile', files[0])
      setModalOpen((prev) => ({ ...prev, blackLogoOpen: true }))
    }
  }

  const handleDragOver = useCallback((e: any) => {
    e.preventDefault()
  }, [])

  const handleFileInputClick = (type: FileType) => {
    if (type === 'principal') {
      principalLogoRef.current?.click()
    } else if (type === 'branco') {
      whiteLogoRef.current?.click()
    } else if (type === 'preto') {
      blackLogoRef.current?.click()
    }
  }

  return (
    <>
      <Typography variant='h6'>
        Faça o upload de 3 logos de sua empresa na seguinte ordem, padrão, todo branco e todo preto, com no
        minimo 100x100
      </Typography>

      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={4}
        >
          <UploadBox
            type='principal'
            preview={watch('baseLogo')}
            reference={principalLogoRef as any}
            onAddFile={handleAddFile}
            onDrop={handleFileSelect}
            onDragOver={handleDragOver}
            onRemoveFile={handleRemoveFile}
            onFileInputClick={handleFileInputClick}
          />
        </Grid>
        <Grid
          item
          xs={4}
        >
          <UploadBox
            type='branco'
            preview={watch('whiteLogo')}
            reference={whiteLogoRef as any}
            onAddFile={handleAddFile}
            onDrop={handleFileSelect}
            onDragOver={handleDragOver}
            onRemoveFile={handleRemoveFile}
            onFileInputClick={handleFileInputClick}
          />
        </Grid>
        <Grid
          item
          xs={4}
        >
          <UploadBox
            type='preto'
            preview={watch('blackLogo')}
            reference={blackLogoRef as any}
            onAddFile={handleAddFile}
            onDrop={handleFileSelect}
            onDragOver={handleDragOver}
            onRemoveFile={handleRemoveFile}
            onFileInputClick={handleFileInputClick}
          />
        </Grid>
      </Grid>

      <ModalCropTheme
        file={watch('baseLogoFile')}
        open={modalOpen.baseLogoOpen}
        onClose={() => setModalOpen((prev) => ({ ...prev, baseLogoOpen: false }))}
        onCrop={(file) => {
          createUrlPreview(file, 'baseLogo')
          setValue('baseLogoFile', file)
        }}
      />

      <ModalCropTheme
        file={watch('whiteLogoFile')}
        open={modalOpen.whiteLogoOpen}
        onClose={() => setModalOpen((prev) => ({ ...prev, whiteLogoOpen: false }))}
        onCrop={(file) => {
          createUrlPreview(file, 'whiteLogo')
          setValue('whiteLogoFile', file)
        }}
      />

      <ModalCropTheme
        file={watch('blackLogoFile')}
        open={modalOpen.blackLogoOpen}
        onClose={() => setModalOpen((prev) => ({ ...prev, blackLogoOpen: false }))}
        onCrop={(file) => {
          createUrlPreview(file, 'blackLogo')
          setValue('blackLogoFile', file)
        }}
      />
    </>
  )
}

interface UploadBoxProps {
  type: FileType
  preview: string
  reference: MutableRefObject<HTMLInputElement>
  onFileInputClick: (type: FileType) => void
  onDrop: (e: DragEvent<HTMLDivElement>, type: FileType) => void
  onDragOver: (e: DragEvent<HTMLDivElement>) => void
  onAddFile: (e: ChangeEvent<HTMLInputElement>, type: FileType) => void
  onRemoveFile: (type: FileType) => void
}

const UploadBox: React.FC<UploadBoxProps> = ({
  onFileInputClick,
  onRemoveFile,
  onDragOver,
  onAddFile,
  reference,
  preview,
  onDrop,
  type,
}) => {
  const classes = useStyles()

  const previewValidation = preview != null && preview != ''

  const havePreviewClassName = (preview: null | string, type: FileType) => {
    if (!previewValidation) {
      return classes.formBox
    }

    if (type === 'branco') {
      return classes.formBoxWhite
    }

    return classes.formBoxBase
  }

  const handleFileInputClick = () => {
    if (!previewValidation) {
      onFileInputClick(type)
    }
  }

  return (
    <Box
      sx={{ cursor: previewValidation ? 'default' : 'pointer' }}
      className={havePreviewClassName(preview, type)}
      onDrop={(e) => onDrop(e, type)}
      onDragOver={onDragOver}
      onClick={handleFileInputClick}
    >
      <input
        name='file'
        hidden
        type='file'
        multiple={false}
        ref={reference}
        onChange={(e) => onAddFile(e, type)}
      />
      {previewValidation ? (
        <>
          <img
            src={preview}
            alt='logo'
          />
          <span
            className={classes.deleteButton}
            onClick={() => onRemoveFile(type)}
          >
            <DeleteOutline />
          </span>
        </>
      ) : (
        <Box className={classes.boxText}>
          <AddPhotoAlternateOutlined fontSize='large' />
          <span>Arrastar e Solte o logo {type} aqui</span>
        </Box>
      )}
    </Box>
  )
}

export default LogoUpload
